import {
    booleanAttribute,
    computed,
    Directive,
    ElementRef,
    HostListener,
    input,
} from "@angular/core";
import { ControlValueAccessor } from "@angular/forms";

const stringToNumber = (value: string | number): number => {
    const result = Number(String(value || "0").replace(/,/g, ""));
    return isNaN(result) ? 0 : result;
};

@Directive({
    selector: "[inputNumber]",
})
export class InputNumberDirective implements ControlValueAccessor {
    private onChange = (_: any) => {};
    private onTouched = () => {};
    private el: HTMLInputElement;
    private lastSanitizedValue = ""; // ✅ 최근 정제된 값 저장

    currencySymbol = input<string>("");
    inputNumber = input<boolean, string>(true, { transform: booleanAttribute });
    comma = input<boolean, string>(true, { transform: booleanAttribute });

    _max = input<number | null>(null, { alias: "inputNumberMax" });
    max = computed(() => {
        const v = this._max();
        return v != null ? stringToNumber(v) : undefined;
    });

    constructor(private elementRef: ElementRef) {
        this.el = this.elementRef.nativeElement;
    }

    /** ✅ input 이벤트 */
    @HostListener("input", ["$event"])
    onInput(ev: any): void {
        if (!this.inputNumber()) return;

        const rawValue = this.el.value;
        const numeric = this.sanitizeInput(rawValue);

        // ✅ focus 직후 발생한 불필요 input은 무시
        if (numeric === this.lastSanitizedValue) {
            return;
        }

        this.lastSanitizedValue = numeric;

        if (this.isEditing(numeric)) {
            this.el.value = numeric; // 입력 중에는 콤마 없이
            this.onChange(null);
            return;
        }

        let numericValue = parseFloat(numeric);
        if (isNaN(numericValue)) {
            this.el.value = "";
            this.onChange(null);
            return;
        }

        numericValue = this.applyMax(numericValue);
        this.el.value = numeric; // 입력 중에는 콤마 없이
        this.onChange(numericValue);
    }

    /** ✅ focus 시 쉼표 제거 */
    @HostListener("focus")
    onFocus(): void {
        if (!this.inputNumber()) return;
        const sanitized = this.sanitizeInput(this.el.value);
        this.el.value = sanitized;
        this.lastSanitizedValue = sanitized; // ✅ focus 시점 상태 기록
    }

    /** ✅ blur 시 포맷 적용 */
    @HostListener("blur")
    onBlur(): void {
        if (!this.inputNumber()) return;

        const numeric = stringToNumber(this.el.value);
        if (!isNaN(numeric)) {
            const limited = this.applyMax(numeric);
            this.el.value = this.formatValue(limited);
            this.lastSanitizedValue = this.el.value; // ✅ 상태 갱신
            this.onChange(limited);
        } else {
            this.el.value = "";
            this.lastSanitizedValue = "";
            this.onChange(null);
        }
        this.onTouched();
    }

    /** ✅ Form → View */
    writeValue(value: any): void {
        if (!this.inputNumber()) return;
        if (value == null || value === "" || isNaN(value)) {
            this.el.value = "";
            this.lastSanitizedValue = "";
            return;
        }
        value = this.applyMax(value);
        const formatted = this.formatValue(value);
        this.el.value = formatted;
        this.lastSanitizedValue = formatted;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.el.disabled = isDisabled;
    }

    private sanitizeInput(value: string): string {
        return value.replace(/[^0-9.-]/g, "");
    }

    private isEditing(value: string): boolean {
        return value === "" || value === "-" || value === "." || value === "-.";
    }

    private applyMax(value: number): number {
        const maxVal = this.max();
        return typeof maxVal === "number" && !isNaN(value) && value > maxVal
            ? maxVal
            : value;
    }

    private formatValue(value: number): string {
        const absValue = Math.abs(value).toString();
        const [integerPart, decimalPart] = absValue.split(".");
        let formatted = this.formatWithCommas(integerPart || "0");

        if (decimalPart !== undefined) {
            formatted += "." + decimalPart.slice(0, 2);
        }

        if (value < 0) {
            formatted =
                "-" +
                (this.currencySymbol() ? this.currencySymbol() : "") +
                formatted;
        } else if (this.currencySymbol()) {
            formatted = this.currencySymbol() + formatted;
        }

        return formatted;
    }

    private formatWithCommas(value: string): string {
        if (!value) return "0";
        if (!this.comma()) return value;
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
}
